import { useState } from 'react';
import type { FormEvent } from 'react';
import { Eye, EyeOff, LoaderCircle, Lock, Mail } from 'lucide-react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useSession } from '@/auth/session';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface LocationState {
  from?: { pathname: string };
}

/**
 * The one screen every user sees before anything else, so it carries the
 * brand's full weight rather than a bare centred form.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { login, isAuthenticated, loading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!sessionLoading && isAuthenticated) {
    const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/';
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login(email.trim(), password);
      const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'EMAIL_NOT_VERIFIED') {
          setError('Please verify your email before logging in. Check your inbox for the code.');
        } else if (err.code === 'ACCOUNT_LOCKED') {
          setError(err.message);
        } else {
          setError(err.message);
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel — hidden on small screens, where the form needs the room. */}
      <div className="relative hidden overflow-hidden bg-grad-login lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-jade-500/20 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-gold-500/10 blur-3xl"
          aria-hidden
        />

        <div className="relative flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-grad-gold font-display text-2xl font-bold text-ink-950">
            F
          </div>
          <span className="font-display text-2xl tracking-tight text-white">{t('appName')}</span>
        </div>

        <div className="relative max-w-md">
          <p className="font-display text-4xl leading-[1.15] text-white">
            Run the counter.
            <br />
            Not the spreadsheet.
          </p>
          <p className="mt-4 text-ink-300">
            Stock, billing, GST, and expiry — for every pharmacy on the shelf,
            in one place.
          </p>
        </div>

        <p className="relative text-xs text-ink-400">{t('appName')} Inventory · a QMSofts product</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-grad-gold font-display text-xl font-bold text-ink-950">
              F
            </div>
            <span className="font-display text-xl tracking-tight">{t('appName')}</span>
          </div>

          <h1 className="font-display text-3xl">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Log in to your pharmacy.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  placeholder="you@pharmacy.com"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('password')}</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-9"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p
                role="alert"
                className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired"
              >
                {error}
              </p>
            )}

            <Button type="submit" size="touch" className="w-full" disabled={submitting}>
              {submitting && <LoaderCircle className="animate-spin" />}
              {submitting ? 'Logging in…' : t('loginBtn')}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            New pharmacy?{' '}
            <Link to="/signup" className="font-medium text-primary hover:underline">
              Start your free trial
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
